import breadcrumbs from 'assets/img/breadcrumbs.svg';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import { useFetchData } from 'hooks/useFetchData';
import { downloadBlob } from 'backend/additionalInformation';
import { AxiosResponse } from 'axios';
import {
	FormImageUploaderContext,
	IFormImageUploaderContext,
	ILoadFromFilePathOptions,
} from '../formImageUploaderState/formImageUploaderContext';
import ModalLayout from 'components/ModalLayout';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import style from './CropperModal.module.scss';

interface ICropperModal {
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxWidth?: number;
	maxHeight?: number;
	maxFileSizeMB?: number;
	noImageIconId?: string;
	loadFromFilePathOptions?: ILoadFromFilePathOptions;
}

const CropperModal: React.FC<ICropperModal> = ({
	horizontalAspectRatio,
	verticalAspectRatio,
	maxWidth,
	maxHeight,
	maxFileSizeMB,
	noImageIconId = 'menu',
	loadFromFilePathOptions,
}) => {
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setFullSizeSrc,
		uploadImage,
		setLoadingImageError,
		imageOrientation,
		fullSizeSrc,
		typeOfModalWindow,
	} = useContext<IFormImageUploaderContext>(FormImageUploaderContext);
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const [isCropAreaChanged, setCropAreaChanged] = useState<boolean>(false);
	const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [loadingImageErrorInModal, setLoadingImageErrorInModal] = useState<boolean>(false);
	const [imageFile, setImageFile] = useState<File | Blob | null>(null);

	const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

	// Download image blob from s3, then set img src from localhost to avoid tainted canvas exception (edit forms)
	const { response, refetch } = useFetchData<AxiosResponse<Blob>, string>(
		loadFromFilePathOptions?.filePath ? downloadBlob : null,
		loadFromFilePathOptions?.filePath || '',
	);

	useEffect(() => {
		if (response) {
			const urlFromBlob = URL.createObjectURL(response.data);
			setFullSizeSrc(urlFromBlob);
			setImageFile(response.data);
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [loadFromFilePathOptions?.filePath]);

	// If photo exists, set it as full size src (edit forms)
	useEffect(() => {
		loadFromFilePathOptions && setFullSizeSrc(loadFromFilePathOptions.filePath);
	}, [loadFromFilePathOptions?.filePath]);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		setLoadingImageErrorInModal(false);
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = e.target.files ? e.target.files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB) {
			changeTypeOfErrorModalWindow('fileSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			const sourceUrl = URL.createObjectURL(imgFile);
			setFullSizeSrc(sourceUrl);
			setImageFile(imgFile);
		} else {
			changeTypeOfErrorModalWindow('invalidFileFormat');
		}
		e.target.value = '';
	};

	const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (canvasRef.current && imageRef.current) {
			canvasRef.current.width = imageRef.current.naturalWidth;
			canvasRef.current.height = imageRef.current.naturalHeight;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
		}
	};

	const handleCropAreaChange = (c: PixelCrop) => {
		setCrop(c);
		isSentOriginalImg && setSentOriginalImg(false);
		if (canvasRef.current && imageRef.current) {
			setCropAreaChanged(true);
			canvasRef.current.width = c.width;
			canvasRef.current.height = c.height;
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(
				imageRef.current,
				c.x * scaleX,
				c.y * scaleY,
				c.width * scaleX,
				c.height * scaleY,
				0,
				0,
				c.width,
				c.height,
			);
		}
	};

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setCrop(undefined);
		setCropAreaChanged(false);
		setSentOriginalImg(true);
	};

	const handleApplyImageSize = () => {
		if (canvasRef.current?.width === 0 && canvasRef.current.height === 0 && imageFile) {
			uploadImage(imageFile);
		} else if (isSentOriginalImg && imageFile) {
			uploadImage(imageFile);
		} else {
			canvasRef.current?.toBlob(
				(blob) => {
					if (blob) {
						uploadImage(blob);
					}
				},
				'image/jpeg',
				0.8,
			);
		}
		setLoadingImageError(false);
		closeModal();
	};

	const showErrorImage = () => {
		setLoadingImageErrorInModal(true);
	};

	const renderImage = () => {
		if (!fullSizeSrc) {
			return (
				<div className={style.noImageContainer}>
					<svg className={style.noImageIcon}>
						<use xlinkHref={`${breadcrumbs}#${noImageIconId}`} />
					</svg>
				</div>
			);
		}

		if (loadingImageErrorInModal) {
			return (
				<div className={style.brokenImageContainer}>
					<svg className={style.brokenImageIcon}>
						<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
					</svg>
					<div>Error image</div>
				</div>
			);
		}

		return (
			<ReactCrop
				className={style.imageContainer}
				crop={crop}
				aspect={aspectRatio}
				maxWidth={maxWidth}
				maxHeight={maxHeight}
				onChange={handleCropAreaChange}>
				<img ref={imageRef} src={fullSizeSrc} onLoad={drawImageInCanvas} onError={showErrorImage} alt="Photo" />
			</ReactCrop>
		);
	};

	return (
		<ModalLayout width="60%" isVisible={typeOfModalWindow === 'cropper'} setIsShowModal={closeModal}>
			<div className={style.content}>
				{renderImage()}
				<input
					id="imageLoader"
					className={style.uploadInput}
					type="file"
					accept="image/jpeg,image/png"
					onChange={handleFileChange}
				/>
				<div className={style.buttons}>
					<label className={style.uploadLabel} htmlFor="imageLoader">
						<Button className={style.uploadButton}>
							<Text translationNameSpace="form" translationKey="chooseImageButton" asFragment={true} />
						</Button>
					</label>
					<Button onClick={handleApplyImageSize} disabled={!fullSizeSrc || !isCropAreaChanged}>
						<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
					</Button>
				</div>
			</div>
			<canvas className={style.canvas} ref={canvasRef} />
		</ModalLayout>
	);
};

export default CropperModal;
